import { Card, CardContent } from "@/components/ui/card";
import { tr } from "@/utils/i18n";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription,
  DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { FlaskConical, Clock, Package, DollarSign } from "lucide-react";

const clickText = (lang) =>
  lang === 'pt' ? 'Clique para detalhes' : lang === 'es' ? 'Clic para detalles' : 'Click for details';

function StatDialog({ testId, Icon, value, label, color, title, description, items, totalLabel, totalValue, language }) {
  const ring = {
    cyan: 'hover:border-cyan-500/50 hover:shadow-[0_0_15px_rgba(6,182,212,0.15)]',
    violet: 'hover:border-violet-500/50 hover:shadow-[0_0_15px_rgba(139,92,246,0.15)]',
    green: 'hover:border-green-500/50 hover:shadow-[0_0_15px_rgba(34,197,94,0.15)]',
    amber: 'hover:border-amber-500/50 hover:shadow-[0_0_15px_rgba(245,158,11,0.15)]',
  };
  const bg = {
    cyan: 'bg-cyan-500/20 text-cyan-400',
    violet: 'bg-violet-500/20 text-violet-400',
    green: 'bg-green-500/20 text-green-400',
    amber: 'bg-amber-500/20 text-amber-400',
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Card className={`bg-zinc-950/50 border-zinc-800 cursor-pointer transition-all ${ring[color]}`} data-testid={testId}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${bg[color]} flex items-center justify-center`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{value}</p>
                <p className="text-xs text-zinc-500">{label}</p>
              </div>
            </div>
            <p className={`text-xs ${bg[color].split(' ')[1]}/70 mt-2 text-right`}>{clickText(language)}</p>
          </CardContent>
        </Card>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Icon className={`w-5 h-5 ${bg[color].split(' ')[1]}`} />
            {title}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-4">
          {items?.map((item) => (
            <div key={item.name} className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <div className="flex items-center justify-between">
                <span className="font-medium text-white">{item.name}</span>
                <Badge className={bg[color]}>{item.detail}</Badge>
              </div>
              <p className="text-xs text-zinc-500 mt-1">{item.sub}</p>
            </div>
          ))}
          {totalLabel && (
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <div className="flex items-center justify-between">
                <span className="font-bold text-white">{totalLabel}</span>
                <span className="text-2xl font-bold text-green-400">{totalValue}</span>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function StatsOverview({ cycleData, language, t }) {
  const peptides = cycleData.peptides || [];
  const totalVials = peptides.reduce((acc, p) => acc + p.vials_needed, 0);

  const peptidesItems = peptides.map(p => ({
    name: p.name,
    detail: p.dosage_mg ? `${p.dosage_mg}mg` : `${p.dosage_iu}IU`,
    sub: p.timing
  }));
  const weeksItems = peptides.map(p => ({
    name: p.name,
    detail: `${p.cycle_duration_weeks} ${t("weeks")}`,
    sub: p.frequency
  }));
  const vialsItems = peptides.map(p => ({
    name: p.name,
    detail: `${p.vials_needed} ${t("vials")}`,
    sub: `$${p.cost_per_vial_usd}/vial`
  }));
  const costItems = peptides.map(p => ({
    name: p.name,
    detail: `$${p.total_cost_usd}`,
    sub: `${p.vials_needed} x $${p.cost_per_vial_usd}`
  }));

  const titles = {
    peptides: tr(language, "Peptides in Cycle", "Peptídeos no Ciclo", "Péptidos en el Ciclo"),
    weeks: tr(language, "Cycle Duration", "Duração do Ciclo", "Duración del Ciclo"),
    vials: tr(language, "Vials Needed", "Frascos Necessários", "Viales Necesarios"),
    cost: tr(language, "Cost Breakdown", "Detalhes de Custo", "Detalles de Costo"),
  };
  const descs = {
    peptides: tr(language, "All peptides in this cycle", "Todos os peptídeos neste ciclo", "Todos los péptidos en este ciclo"),
    weeks: tr(language, "Duration of each peptide", "Duração de cada peptídeo", "Duración de cada péptido"),
    vials: tr(language, "Vials needed per peptide", "Frascos necessários por peptídeo", "Viales necesarios por péptido"),
    cost: tr(language, "Detailed cost per peptide", "Custo detalhado por peptídeo", "Costo detallado por péptido"),
  };
  const totalLabel = tr(language, "Estimated Total", "Total Estimado", "Total Estimado");

  return (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-overview">
      <StatDialog
        testId="stat-peptides" Icon={FlaskConical} color="cyan"
        value={peptides.length} label="Peptides"
        title={titles.peptides} description={descs.peptides}
        items={peptidesItems} language={language}
      />
      <StatDialog
        testId="stat-weeks" Icon={Clock} color="violet"
        value={cycleData.total_duration_weeks} label={t("weeks")}
        title={titles.weeks} description={descs.weeks}
        items={weeksItems} language={language}
      />
      <StatDialog
        testId="stat-vials" Icon={Package} color="green"
        value={totalVials} label={t("vials")}
        title={titles.vials} description={descs.vials}
        items={vialsItems} language={language}
      />
      <StatDialog
        testId="stat-cost" Icon={DollarSign} color="amber"
        value={`$${cycleData.total_cost_usd}`} label={t("totalCost")}
        title={titles.cost} description={descs.cost}
        items={costItems} language={language}
        totalLabel={totalLabel} totalValue={`$${cycleData.total_cost_usd}`}
      />
    </section>
  );
}
